import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, Lock, Bell, User as UserIcon } from "lucide-react";

const defaultPrefs = { campaigns: true, mailings: true, invoices: true, activities: true };

export default function AssocProfile() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState({ name: "", notification_prefs: defaultPrefs });
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      setProfile({
        name: data.name || "",
        notification_prefs: { ...defaultPrefs, ...(data.notification_prefs || {}) },
      });
    });
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me", profile);
      toast.success("Perfil actualitzat");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pw.new_password !== pw.confirm) return toast.error("Les contrasenyes no coincideixen");
    if (pw.new_password.length < 8) return toast.error("Mínim 8 caràcters");
    setChanging(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Contrasenya canviada");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setChanging(false); }
  };

  const togglePref = (key) => setProfile({ ...profile, notification_prefs: { ...profile.notification_prefs, [key]: !profile.notification_prefs[key] } });

  return (
    <div className="space-y-8 max-w-3xl" data-testid="assoc-profile">
      <div>
        <div className="overline text-accent">Compte</div>
        <h1 className="mt-2 text-3xl font-heading font-bold tracking-tight">El meu perfil</h1>
        <p className="text-sm text-muted-foreground mt-2">Gestiona les teves dades personals, la contrasenya i les preferències de notificació.</p>
      </div>

      <Card className="rounded-2xl border-2">
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div className="flex items-center gap-2 text-sm font-heading font-semibold uppercase tracking-widest text-muted-foreground">
            <UserIcon className="w-4 h-4" aria-hidden="true" /> Dades personals
          </div>
          <div>
            <Label>Nom</Label>
            <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-2 rounded-xl" data-testid="profile-name-input" />
          </div>
          <div>
            <Label>Correu electrònic</Label>
            <Input value={user?.email || ""} disabled className="mt-2 rounded-xl opacity-70" />
            <p className="text-xs text-muted-foreground mt-1">L'email no es pot canviar aquí. Contacta amb l'administració.</p>
          </div>
          <Button onClick={saveProfile} disabled={saving} className="rounded-full" data-testid="profile-save-btn">
            <Save className="w-4 h-4 mr-2" aria-hidden="true" /> {saving ? "Desant…" : "Desar canvis"}
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-2">
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div className="flex items-center gap-2 text-sm font-heading font-semibold uppercase tracking-widest text-muted-foreground">
            <Lock className="w-4 h-4" aria-hidden="true" /> Canviar contrasenya
          </div>
          <div>
            <Label>Contrasenya actual</Label>
            <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-2 rounded-xl" data-testid="profile-pw-current" autoComplete="current-password" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Nova contrasenya</Label>
              <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-2 rounded-xl" data-testid="profile-pw-new" autoComplete="new-password" />
            </div>
            <div>
              <Label>Confirma</Label>
              <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-2 rounded-xl" data-testid="profile-pw-confirm" autoComplete="new-password" />
            </div>
          </div>
          <Button onClick={changePassword} disabled={changing || !pw.current_password || !pw.new_password} className="rounded-full" data-testid="profile-pw-btn">
            <Lock className="w-4 h-4 mr-2" aria-hidden="true" /> {changing ? "Canviant…" : "Canviar contrasenya"}
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-2">
        <CardContent className="p-6 sm:p-8 space-y-5">
          <div className="flex items-center gap-2 text-sm font-heading font-semibold uppercase tracking-widest text-muted-foreground">
            <Bell className="w-4 h-4" aria-hidden="true" /> Notificacions per correu
          </div>
          {[
            { k: "campaigns", label: "Noves campanyes de l'associació" },
            { k: "mailings", label: "Newsletters i comunicacions" },
            { k: "invoices", label: "Factures i recordatoris de pagament" },
            { k: "activities", label: "Reunions i activitats" },
          ].map(({ k, label }) => (
            <div key={k} className="flex items-center justify-between py-2 border-b border-border last:border-b-0">
              <div className="text-sm">{label}</div>
              <Switch checked={!!profile.notification_prefs[k]} onCheckedChange={() => togglePref(k)} data-testid={`profile-pref-${k}`} />
            </div>
          ))}
          <Button onClick={saveProfile} disabled={saving} variant="outline" className="rounded-full" data-testid="profile-prefs-save-btn">
            <Save className="w-4 h-4 mr-2" aria-hidden="true" /> Desar preferències
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
